import type {
  DetectiveEnvelope as Envelope,
  EvidenceRef,
  Verdict,
} from '@/types/askAi';

const VERDICT_COLOUR: Record<Verdict, string> = {
  refuse: 'bg-red-50 text-red-800 border-red-200',
  inconclusive: 'bg-amber-50 text-amber-800 border-amber-200',
  probable: 'bg-blue-50 text-blue-800 border-blue-200',
  confident: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};

// Evidence groups render in a stable order regardless of the array order the
// server sent.
const EVIDENCE_KIND_ORDER: EvidenceRef['kind'][] = ['row', 'metric', 'chunk'];
const EVIDENCE_KIND_LABEL: Record<EvidenceRef['kind'], string> = {
  row: 'Rows',
  metric: 'Metrics',
  chunk: 'Context',
};

// Aggregate metrics surfaced in the collapsed <details>, in this order. Only
// the ones actually present are shown.
const SHOWN_METRICS: Array<{ key: string; label: string }> = [
  { key: 'row_count', label: 'rows' },
  { key: 'critical_count', label: 'critical' },
  { key: 'null_geo_pct', label: 'null geo %' },
  { key: 'min_baseline_sample_size', label: 'min baseline n' },
];

// A sanctions hit is advisory only — never a confirmed finding. Highlight it
// (amber + alert glyph) and stamp the fixed disclaimer, matching the server's
// `_sanction_evidence_refs` wording.
function isSanctionRef(ev: EvidenceRef): boolean {
  return (
    ev.column === 'sanction_match' ||
    (ev.why_it_matters ?? '').toLowerCase().includes('sanctions')
  );
}

function EvidenceItem({ ev }: { ev: EvidenceRef }) {
  const sanction = isSanctionRef(ev);
  return (
    <li
      className={
        sanction
          ? 'rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-800'
          : ''
      }
    >
      <div className="flex items-baseline gap-1">
        {sanction && (
          <span aria-hidden className="shrink-0">
            ⚠
          </span>
        )}
        {ev.column && (
          <span className="font-mono text-[0.7rem] text-muted-foreground">
            {ev.column}
            {typeof ev.row_index === 'number' ? `[${ev.row_index}]` : ''} ·{' '}
          </span>
        )}
        <span className="font-medium break-all">{ev.value}</span>
      </div>
      {ev.why_it_matters && (
        <div className="text-xs text-muted-foreground">{ev.why_it_matters}</div>
      )}
      {sanction && (
        <div className="text-[0.7rem] font-medium uppercase tracking-wide text-amber-700">
          advisory, not confirmed
        </div>
      )}
    </li>
  );
}

interface DetectiveEnvelopeProps {
  env: Envelope;
}

export function DetectiveEnvelope({ env }: DetectiveEnvelopeProps) {
  const metricsToShow = SHOWN_METRICS.filter(
    (m) => env.computed_metrics && env.computed_metrics[m.key] !== undefined,
  );

  return (
    <div className="space-y-2 rounded-lg border bg-background/60 px-3 py-2">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${VERDICT_COLOUR[env.verdict]}`}
        >
          {env.verdict.toUpperCase()} · {env.confidence_score}/100
        </span>
        {env.why_this_score && (
          <span className="truncate text-xs text-muted-foreground">
            {env.why_this_score}
          </span>
        )}
      </div>

      {env.reasoning.length > 0 && (
        <ol className="ml-4 list-decimal space-y-0.5 text-sm">
          {env.reasoning.map((s) => (
            <li key={s.step}>
              {s.claim}
              {s.supporting_evidence_refs.length > 0 && (
                <sup className="ml-0.5 text-[0.65rem] text-muted-foreground">
                  {s.supporting_evidence_refs.map((ref, i) => (
                    <span key={ref}>
                      {i > 0 && ','}
                      <span title={ref}>{i + 1}</span>
                    </span>
                  ))}
                </sup>
              )}
            </li>
          ))}
        </ol>
      )}

      {env.evidence.length > 0 && (
        <div className="space-y-1.5 text-sm">
          {EVIDENCE_KIND_ORDER.map((kind) => {
            const group = env.evidence.filter((e) => e.kind === kind);
            if (group.length === 0) return null;
            return (
              <div key={kind}>
                <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
                  {EVIDENCE_KIND_LABEL[kind]}
                </div>
                <ul className="ml-1 space-y-1">
                  {group.map((ev, i) => (
                    <EvidenceItem key={`${kind}-${i}`} ev={ev} />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {metricsToShow.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none hover:text-foreground">
            Computed metrics
          </summary>
          <dl className="ml-4 mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5">
            {metricsToShow.map((m) => (
              <div key={m.key} className="flex justify-between gap-2">
                <dt>{m.label}</dt>
                <dd className="font-mono">{env.computed_metrics[m.key]}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      {env.counter_hypotheses.length > 0 && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none hover:text-foreground">
            Counter-hypotheses ({env.counter_hypotheses.length})
          </summary>
          <ul className="ml-4 mt-1 list-disc">
            {env.counter_hypotheses.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        </details>
      )}

      {env.next_investigation_step.body && (
        <div className="text-xs">
          <strong>Next:</strong> {env.next_investigation_step.body}
        </div>
      )}
    </div>
  );
}

export default DetectiveEnvelope;
